import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subject, Subscription, debounceTime, of, switchMap } from 'rxjs';
import { AssetManagementItem, RegistryItem } from 'src/app/api/models';
import { RegistryService, WarehouseService } from 'src/app/api/services';

// Tipo di contatto "Fornitore"
const REGISTRY_TYPE_FORNITORE = 2;

export type DisposeMode = 'DISPOSE' | 'DDT';

// Smaltimento: porta fuori dal sistema gli asset che sono in una sede RAEE. L'uscita è definitiva,
// quindi servono le note (es. numero del FIR) e una conferma esplicita.
// Due modalità: 'DISPOSE' con il solo scarico, 'DDT' con un DDT verso un fornitore per ogni magazzino.
// data: { assets: AssetManagementItem[], mode?: DisposeMode }; si chiude con
// { done: true, count, ddts? } se lo smaltimento è registrato.
@Component({
  selector: 'app-modal-dispose-raee',
  templateUrl: './modal-dispose-raee.component.html',
  styleUrls: ['./modal-dispose-raee.component.scss'],
})
export class ModalDisposeRaeeComponent implements OnInit, OnDestroy {
  assets: AssetManagementItem[];
  mode: DisposeMode;

  notes = new FormControl<string>('', { nonNullable: true });
  acknowledged = new FormControl<boolean>(false, { nonNullable: true });
  supplier = new FormControl<RegistryItem | null>(null);
  suggestedSuppliers: RegistryItem[] = [];

  isLoading = false;
  errorMessage?: string;

  private supplierQuery$ = new Subject<string>();
  private subs = new Subscription();

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private warehouseService: WarehouseService,
    private registryService: RegistryService
  ) {
    this.assets = config.data!.assets;
    this.mode = config.data!.mode ?? 'DISPOSE';
  }

  ngOnInit() {
    if (this.mode !== 'DDT') return;
    this.subs.add(
      this.supplierQuery$
        .pipe(
          debounceTime(300),
          switchMap(query =>
            query
              ? this.registryService.registryControllerGetAll({
                  limit: 15,
                  skip: 0,
                  contact_types: [REGISTRY_TYPE_FORNITORE],
                  query,
                })
              : of({ data: { list: [] as RegistryItem[] } })
          )
        )
        .subscribe(res => (this.suggestedSuppliers = res.data.list))
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  filterSuppliers(event: AutoCompleteCompleteEvent) {
    this.supplierQuery$.next(event.query);
  }

  // numero di commesse diverse tra gli asset scelti
  get agreementsCount(): number {
    return new Set(this.assets.map(a => a.related_agreement?.id)).size;
  }

  // sedi RAEE diverse, cioè magazzini: con il DDT se ne crea uno per ciascuna
  get warehousesCount(): number {
    return new Set(this.assets.map(a => a.related_warehouse?.id)).size;
  }

  get canSubmit(): boolean {
    return (
      !this.isLoading &&
      this.assets.length > 0 &&
      this.notes.value.trim().length > 0 &&
      this.acknowledged.value &&
      (this.mode !== 'DDT' || !!this.supplier.value?.id)
    );
  }

  close() {
    this.ref.close();
  }

  submit() {
    if (!this.canSubmit) return;
    this.isLoading = true;
    this.errorMessage = undefined;
    const asset_ids = this.assets.map(a => a.id);
    const notes = this.notes.value.trim();

    if (this.mode === 'DDT') {
      // il corpo della risposta non è nel client generato: i DDT creati arrivano nell'header
      this.warehouseService
        .warehouseControllerCreateDisposeDdt$Response({
          body: { asset_ids, notes, supplier_registry_id: this.supplier.value!.id },
        })
        .subscribe({
          next: res => {
            this.isLoading = false;
            this.ref.close({
              done: true,
              count: this.assets.length,
              ddts: this.parseDdts(res.headers.get('x-dispose-ddts')),
            });
          },
          error: err => this.onError(err),
        });
      return;
    }

    this.warehouseService.warehouseControllerCreateDispose({ body: { asset_ids, notes } }).subscribe({
      next: () => {
        this.isLoading = false;
        this.ref.close({ done: true, count: this.assets.length });
      },
      error: err => this.onError(err),
    });
  }

  private parseDdts(header: string | null): { id: number; identifier: string }[] {
    if (!header) return [];
    try {
      return JSON.parse(decodeURIComponent(header));
    } catch {
      return [];
    }
  }

  private onError(err: any) {
    this.isLoading = false;
    // con responseType text il corpo dell'errore può arrivare come stringa JSON
    let body = err?.error;
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch {
        body = undefined;
      }
    }
    this.errorMessage =
      (typeof body?.message === 'string' && body.message) ||
      body?.message?.message ||
      'Smaltimento non riuscito.';
  }
}
